import React, { useState, useMemo } from 'react';
import { Table, Input, Select, Button, DatePicker, message, Typography } from 'antd';
import type { TableColumnsType } from 'antd';
import { useTranslation } from 'react-i18next';
import type { Dayjs } from 'dayjs';
import { useQuery } from '@apollo/client';
import TablePaginationBar from '../../../../components/TablePaginationBar';
import { useFilterContainerClassName, useFilterInputContainerClassName } from '../../../../utils/languageUtils';
import { getSmartLabelClassName } from '../../../../utils/labelWidthUtils';
import { formatTimeRangeToISO, formatTimestampToDateTime } from '../../../../utils/dateTimeUtils';
import { getPlatformPermissionErrorMessage } from '../utils/errorCode';
import {
  QUERY_PERMISSION_OPERATION_LOG_BY_PAGE,
  type PermissionOperationLogQueryConditionInput,
  type PermissionOperationLogItemType,
  type QueryPermissionOperationLogByPageResponse,
} from './graphql/queries/queryPermissionOperationLogByPage';

const PAGE_SIZE_OPTIONS = ['10', '20', '50'];

type BizType = 1 | 2 | 3 | 4 | 5;
type OperationType = 1 | 2 | 3;

type ChangeItem = {
  fieldName?: string | null;
  oldValueShow?: string | null;
  newValueShow?: string | null;
};

const FIELD_LABEL_KEY_MAP_BY_BIZ_TYPE: Partial<Record<BizType, Record<string, string>>> = {
  // 你稍后提供各业务类型的 fieldName -> 抽屉 label 映射后，我会在这里补全
  1: {
    code: 'central.platformPermission.resource.fields.code',
    name: 'central.platformPermission.resource.fields.name',
    type: 'central.platformPermission.resource.fields.type',
    parentId: 'central.platformPermission.resource.parentResource',
    description: 'central.platformPermission.resource.fields.description',
  }, // RESOURCE
  2: {
    name: 'central.platformPermission.permissionPackage.fields.name',
    description: 'central.platformPermission.permissionPackage.fields.description',
    resourceIds: 'central.platformPermission.permissionPackage.fields.resources',
  }, // PERMISSION_PACKAGE
  3: {
    name: 'central.platformPermission.role.fields.name',
    description: 'central.platformPermission.role.fields.description',
    packageIds: 'central.platformPermission.role.fields.packageIds',
  }, // ROLE
  4: {
    name: 'central.platformPermission.organization.modal.addNewName',
    parentId: 'central.platformPermission.organization.modal.parentId',
    warehouseList: 'central.platformPermission.organization.modal.warehouseList',
  }, // DEPT
  5: {
    name: 'central.personnel.pageDetails.name',
    email: 'central.personnel.pageDetails.email',
    phone: 'central.personnel.pageDetails.phone',
    password: 'central.personnel.pageDetails.password',
    status: 'central.personnel.pageDetails.status',
    deptIds: 'central.platformPermission.personnel.fields.deptIds',
    roleIds: 'central.platformPermission.personnel.fields.roleIds',
  }, // ACCOUNT
};

function safeParseChangesJson(changesJson: string | null | undefined): ChangeItem[] {
  if (!changesJson) return [];
  try {
    const parsed = JSON.parse(changesJson) as unknown;
    if (Array.isArray(parsed)) return parsed as ChangeItem[];
    if (parsed && typeof parsed === 'object') {
      const maybeList = (parsed as any).changes;
      if (Array.isArray(maybeList)) return maybeList as ChangeItem[];
    }
    return [];
  } catch {
    return [];
  }
}

function formatChangeList(
  t: (key: string) => unknown,
  bizType: number,
  operationType: number,
  bizId: number,
  changesJson: string | null | undefined
): string {
  const bizTypeKey = bizType as BizType;
  const labelKeyMap = FIELD_LABEL_KEY_MAP_BY_BIZ_TYPE[bizTypeKey] ?? {};
  const list = safeParseChangesJson(changesJson);
  if (list.length === 0) return `ID: ${bizId}；`;

  const isUpdate = operationType === 2;
  const items = list
    .map((c) => {
      const fieldName = String(c.fieldName ?? '').trim();
      if (!fieldName) return null;
      const labelKey = labelKeyMap[fieldName];
      const label = labelKey ? (t(labelKey) as string) : fieldName;
      const rawOldShow = c.oldValueShow != null ? String(c.oldValueShow) : '';
      const rawNewShow = c.newValueShow != null ? String(c.newValueShow) : '';

      // 资源管理：type 字段的值需要国际化（MENU/BUTTON）
      const translateResourceType = (value: string): string => {
        const v = value.trim();
        if (!v) return '';
        if (v === 'MENU') return (t('central.platformPermission.resource.typeOptions.menu') as string) || v;
        if (v === 'BUTTON') return (t('central.platformPermission.resource.typeOptions.button') as string) || v;
        return value;
      };
      const translateResourceParentId = (value: string): string =>
        value === '' ? (t('central.platformPermission.resource.noParent') as string) : value;
      let oldShow = bizType === 1 && fieldName === 'type' ? translateResourceType(rawOldShow) : rawOldShow;
      let newShow = bizType === 1 && fieldName === 'type' ? translateResourceType(rawNewShow) : rawNewShow;
      if (bizType === 1 && fieldName === 'parentId') {
        oldShow = translateResourceParentId(oldShow);
        newShow = translateResourceParentId(newShow);
      }
      // 人员管理：status 字段的值需要国际化（0/1）
      const translateAccountStatus = (value: string): string => {
        const v = value.trim();
        if (!v) return '';
        if (v === '1') return (t('central.platformPermission.personnel.filters.statusActive') as string) || v;
        if (v === '0') return (t('central.platformPermission.personnel.filters.statusInactive') as string) || v;
        return value;
      };
      const oldShow2 = bizType === 5 && fieldName === 'status' ? translateAccountStatus(oldShow) : oldShow;
      const newShow2 = bizType === 5 && fieldName === 'status' ? translateAccountStatus(newShow) : newShow;
      if (isUpdate) {
        if (!oldShow2 && !newShow2) return null;
        if (!oldShow2 || oldShow2 === newShow2) return `${label}: ${newShow2 || oldShow2}`;
        if (!newShow2) return `${label}: ${oldShow2}`;
        return `${label}: ${oldShow2} -> ${newShow2}`;
      }
      // 新增 / 删除：只取 newValueShow
      if (!newShow2) return null;
      return `${label}: ${newShow2}`;
    })
    .filter((x): x is string => Boolean(x));

  if (items.length === 0) return `ID: ${bizId}；`;
  return `ID: ${bizId}；${items.join('；')}`;
}

export interface FilterState {
  timeRange: [Dayjs | null, Dayjs | null] | null;
  operatorName: string;
  operationType: OperationType | '';
  bizId: string;
  bizType: BizType | '';
}

const DEFAULT_FILTERS: FilterState = {
  timeRange: null,
  operatorName: '',
  operationType: '',
  bizId: '',
  bizType: '',
};

const PermissionOperationLogPage: React.FC = () => {
  const { t } = useTranslation();
  const containerClassName = useFilterContainerClassName();
  const inputContainerClassName = useFilterInputContainerClassName();
  const [draftFilters, setDraftFilters] = useState<FilterState>(DEFAULT_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState<FilterState>(DEFAULT_FILTERS);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const operationTypeOptions = [
    { value: 1, label: t('central.platformPermission.permissionOperationLog.actions.add') },
    { value: 2, label: t('central.platformPermission.permissionOperationLog.actions.update') },
    { value: 3, label: t('central.platformPermission.permissionOperationLog.actions.delete') },
  ];

  const bizTypeOptions = [
    { value: 1, label: t('central.platformPermission.permissionOperationLog.bizTypes.resource') },
    { value: 2, label: t('central.platformPermission.permissionOperationLog.bizTypes.permissionPackage') },
    { value: 3, label: t('central.platformPermission.permissionOperationLog.bizTypes.role') },
    { value: 4, label: t('central.platformPermission.permissionOperationLog.bizTypes.dept') },
    { value: 5, label: t('central.platformPermission.permissionOperationLog.bizTypes.account') },
  ];

  const graphQLCondition: PermissionOperationLogQueryConditionInput = useMemo(() => {
    const timeRange = appliedFilters.timeRange
      ? ([
          appliedFilters.timeRange[0]?.toISOString() ?? null,
          appliedFilters.timeRange[1]?.toISOString() ?? null,
        ] as [string | null, string | null])
      : null;
    const operatedAtRange = formatTimeRangeToISO(timeRange);
    const bizIdNum = appliedFilters.bizId.trim() ? Number(appliedFilters.bizId.trim()) : null;
    return {
      bizId: Number.isFinite(bizIdNum as number) ? (bizIdNum as number) : null,
      bizType: appliedFilters.bizType || null,
      operationType: appliedFilters.operationType || null,
      operatorName: appliedFilters.operatorName.trim() ? appliedFilters.operatorName.trim() : null,
      operatedAtRange:
        operatedAtRange && operatedAtRange.start && operatedAtRange.end
          ? { start: operatedAtRange.start, end: operatedAtRange.end }
          : null,
    };
  }, [appliedFilters]);

  const variables = useMemo(
    () => ({
      condition: graphQLCondition,
      pageRequest: { page: currentPage, size: pageSize },
    }),
    [graphQLCondition, currentPage, pageSize]
  );

  const { data, loading } = useQuery<QueryPermissionOperationLogByPageResponse>(QUERY_PERMISSION_OPERATION_LOG_BY_PAGE, {
    fetchPolicy: 'network-only',
    variables,
    onError: (e) => {
      const toastText = getPlatformPermissionErrorMessage(t, undefined, e?.message);
      if (toastText) message.error(toastText);
    },
  });

  const page = data?.queryPermissionOperationLogByPage;
  const rows = page?.content ?? [];
  const total = page?.totalElements ?? 0;

  const getOperationLabel = (operationType: number) =>
    operationType === 1
      ? t('central.platformPermission.permissionOperationLog.actions.add')
      : operationType === 2
        ? t('central.platformPermission.permissionOperationLog.actions.update')
        : t('central.platformPermission.permissionOperationLog.actions.delete');

  const getBizTypeLabel = (bizType: number) => bizTypeOptions.find((o) => o.value === bizType)?.label ?? String(bizType);

  const columns: TableColumnsType<PermissionOperationLogItemType> = [
    {
      title: t('central.platformPermission.permissionOperationLog.filters.id'),
      dataIndex: 'id',
      key: 'id',
      width: 100,
      render: (v: number) => <span className="text-black">{v}</span>,
    },
    {
      title: t('central.platformPermission.permissionOperationLog.filters.time'),
      dataIndex: 'operatedAt',
      key: 'operatedAt',
      width: 160,
      render: (v: number) => <span className="text-black">{formatTimestampToDateTime(Number(v))}</span>,
    },
    {
      title: t('central.platformPermission.permissionOperationLog.filters.operator'),
      dataIndex: 'operatorName',
      key: 'operatorName',
      width: 170,
      render: (v: string) => <span className="text-black">{v}</span>,
    },
    {
      title: t('central.platformPermission.permissionOperationLog.filters.action'),
      dataIndex: 'operationType',
      key: 'operationType',
      width: 100,
      render: (v: number) => <span className="text-black">{getOperationLabel(v)}</span>,
    },
    {
      title: t('central.platformPermission.permissionOperationLog.filters.bizType'),
      dataIndex: 'bizType',
      key: 'bizType',
      width: 140,
      render: (v: number) => <span className="text-black">{getBizTypeLabel(v)}</span>,
    },
    {
      title: t('central.platformPermission.permissionOperationLog.filters.content'),
      key: 'content',
      width: 320,
      ellipsis: { showTitle: false },
      render: (_: unknown, record: PermissionOperationLogItemType) => {
        const text = formatChangeList(t, record.bizType, record.operationType, record.bizId, record.changesJson);
        return (
          <Typography.Text
            className="text-black"
            style={{ display: 'block' }}
            ellipsis={{
              tooltip: {
                title: text,
                overlayInnerStyle: { maxHeight: 300, overflow: 'auto' },
              },
            }}
          >
            {text}
          </Typography.Text>
        );
      },
    },
  ];

  const handleQuery = () => {
    setAppliedFilters(draftFilters);
    setCurrentPage(1);
  };

  const handleReset = () => {
    setDraftFilters(DEFAULT_FILTERS);
    setAppliedFilters(DEFAULT_FILTERS);
    setCurrentPage(1);
  };

  const handleTableChange = (page: number, size: number) => {
    setCurrentPage(page);
    setPageSize(size);
  };

  const labelTime = String(t('central.platformPermission.permissionOperationLog.filters.time'));
  const labelOperator = String(t('central.platformPermission.permissionOperationLog.filters.operator'));
  const labelAction = String(t('central.platformPermission.permissionOperationLog.filters.action'));
  const labelBizId = String(t('central.platformPermission.permissionOperationLog.filters.bizId'));
  const labelBizType = String(t('central.platformPermission.permissionOperationLog.filters.bizType'));
  const colLabels = [labelTime, labelOperator, labelAction, labelBizId, labelBizType];

  return (
    <div className="bg-white rounded-lg shadow-sm px-3 pb-3 pt-0">
      <div className="mb-2 pl-0 py-2">
        <div className="grid grid-cols-3 gap-x-10 gap-y-4 items-start">
          <div className="min-w-0">
            <div className={containerClassName}>
              <span className={getSmartLabelClassName(colLabels)}>{labelTime}</span>
              <div className={inputContainerClassName}>
                <DatePicker.RangePicker
                  value={draftFilters.timeRange}
                  onChange={(dates) =>
                    setDraftFilters((f) => ({ ...f, timeRange: dates as [Dayjs | null, Dayjs | null] | null }))
                  }
                  placeholder={[
                    String(t('components.input.selectDate')),
                    String(t('components.input.selectDate')),
                  ]}
                  allowClear
                  className="w-full"
                />
              </div>
            </div>
          </div>
          <div className="min-w-0">
            <div className={containerClassName}>
              <span className={getSmartLabelClassName(colLabels)}>{labelOperator}</span>
              <div className={inputContainerClassName}>
                <Input
                  value={draftFilters.operatorName}
                  onChange={(e) => setDraftFilters((f) => ({ ...f, operatorName: e.target.value }))}
                  placeholder={String(t('components.input.typeIn'))}
                  allowClear
                  className="w-full"
                />
              </div>
            </div>
          </div>
          <div className="min-w-0">
            <div className={containerClassName}>
              <span className={getSmartLabelClassName(colLabels)}>{labelAction}</span>
              <div className={inputContainerClassName}>
                <Select
                  value={draftFilters.operationType || undefined}
                  onChange={(v) => setDraftFilters((f) => ({ ...f, operationType: (v as OperationType) || '' }))}
                  placeholder={t('components.input.select') as string}
                  allowClear
                  options={operationTypeOptions}
                  className="w-full"
                />
              </div>
            </div>
          </div>
          <div className="min-w-0">
            <div className={containerClassName}>
              <span className={getSmartLabelClassName(colLabels)}>{labelBizId}</span>
              <div className={inputContainerClassName}>
                <Input
                  value={draftFilters.bizId}
                  onChange={(e) => setDraftFilters((f) => ({ ...f, bizId: e.target.value }))}
                  placeholder={String(t('components.input.typeIn'))}
                  allowClear
                  className="w-full"
                />
              </div>
            </div>
          </div>
          <div className="min-w-0">
            <div className={containerClassName}>
              <span className={getSmartLabelClassName(colLabels)}>{labelBizType}</span>
              <div className={inputContainerClassName}>
                <Select
                  value={draftFilters.bizType || undefined}
                  onChange={(v) => setDraftFilters((f) => ({ ...f, bizType: (v as BizType) || '' }))}
                  placeholder={t('components.input.select') as string}
                  allowClear
                  options={bizTypeOptions}
                  className="w-full"
                />
              </div>
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button type="primary" onClick={handleQuery} className="w-auto min-w-24">
            <span className="font-bold">{t('components.button.filter')}</span>
          </Button>
          <Button onClick={handleReset} className="w-auto min-w-24">
            <span className="font-bold">{t('components.button.reset')}</span>
          </Button>
        </div>
      </div>

      <Table
        loading={loading}
        rowKey="id"
        columns={columns}
        dataSource={rows}
        pagination={false}
        size="middle"
        tableLayout="fixed"
        className="mb-4"
      />

      <TablePaginationBar
        total={total}
        current={currentPage}
        pageSize={pageSize}
        onChange={handleTableChange}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        showSizeChanger
      />
    </div>
  );
};

export default PermissionOperationLogPage;
